import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { isValidDate } from "../../../utils/checkDateIsValid";
import { getUnitLocation } from "../../../utils/getUnitLocation";

import dayjs from "dayjs";
import moment from "moment";
import { getParoleStatus } from "../../../utils/getParoleStatus";
import useInmate from "../../../hooks/useInmate";
import SubCard from "../../components/cards/SubCard";
import FormContainer from "../../components/FormContainer";
import BasicDatePicker from "../../components/inputs/BasicDatePicker";
import MuiTextField from "../../components/inputs/MuiTextField";
import { useSnackbarHelper } from "../../components/Snackbar";
import BasicTable from "../../components/table/BasicTable";

/**
 * Renders inmate basic info and offense history for a given case.
 * Supports an inline edit mode for updating key inmate fields.
 *
 * @component InmateInfo
 * @param {any} caseData - The full case data object.
 * @param {any} inmate - The inmate object fetched from the case contact.
 * @param {boolean} isDocuSignPending - Whether a DocuSign is pending.
 * @param {Function} updateOffenceHistory - Callback to update offense history.
 * @returns {JSX.Element}
 */
function InmateInfo({
  caseData,
  inmate,
  isDocuSignPending,
  updateOffenceHistory,
  refetchCase,
}: any) {
  const showSnackbar = useSnackbarHelper();
  const { updateInmate, isUpdatingInmate } = useInmate(inmate?._id);

  /** Toggle between view and edit mode for Basic Info */
  const [isEditMode, setIsEditMode] = useState(false);

  const GENDER_OPTIONS = [
    { label: "Male", value: "M" },
    { label: "Female", value: "F" },
    // { label: "Non-binary", value: "non-binary" },
    // { label: "Agender", value: "agender" },
    // { label: "Genderfluid", value: "genderfluid" },
    // { label: "Other", value: "other" },
  ];

  const location = getUnitLocation(inmate?.unit) || "";

  const denialReasonsCodes =
    inmate?.paroleReviewInfo?.denialReasons
      ?.map((item: any) => item.code)
      .join(", ") || "";

  // ── Offense History form ──────────────────────────────────────────────────
  const offenseInitialValues = {
    selectedOffense: caseData?.result?.selectedOffense || "",
    unit: "",
    unit_location: "",
  };

  const offenseFormContext = useForm({
    defaultValues: offenseInitialValues,
  });
  const { watch: offenseWatch, setValue: offenseSetValue } = offenseFormContext;

  const unit = offenseWatch("unit");

  useEffect(() => {
    const unitLocation = getUnitLocation(unit);
    offenseSetValue("unit_location", unitLocation || "");
  }, [unit, offenseSetValue]);

  // ── Basic Info edit form ──────────────────────────────────────────────────
  /**
   * Build default values for the edit form from the current inmate prop.
   * Re-computed whenever inmate changes so the form reflects fresh data.
   */
  const editInitialValues = useMemo(
    () => ({
      dob: inmate?.dob ? dayjs(inmate.dob).toDate() : null,
      age: inmate?.age || "",
      gender: inmate?.gender || "",
      unit: inmate?.unit || "",
      unit_location: getUnitLocation(inmate?.unit) || "",
      // paroleReleaseDate: inmate?.projectedReleaseDate || "",
      // paroleEligibilityDate: inmate?.paroleEligibilityDate || "",
    }),
    [inmate],
  );

  const editFormContext = useForm({
    defaultValues: editInitialValues,
  });

  const {
    watch: editWatch,
    setValue: editSetValue,
    reset: resetEditForm,
  } = editFormContext;

  const editedUnit = editWatch("unit");
  const editedDob = editWatch("dob");

  // Auto-calculate age from DOB
  useEffect(() => {
    if (editedDob) {
      const calculatedAge = moment().diff(moment(editedDob), "years");
      editSetValue("age", calculatedAge);
    }
  }, [editedDob, editSetValue]);

  // Auto-derive unit_location from unit whenever unit changes in edit mode
  useEffect(() => {
    if (isEditMode) {
      const derivedLocation = getUnitLocation(editedUnit);
      editSetValue("unit_location", derivedLocation || "");
    }
  }, [editedUnit, editSetValue, isEditMode]);

  // Re-populate edit form whenever inmate data refreshes
  useEffect(() => {
    resetEditForm(editInitialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inmate]);

  // ── Offense history ───────────────────────────────────────────────────────
  const columns = [
    { label: "Offense Date", value: "offenseDate" },
    { label: "Offense", value: "offense" },
    { label: "Sentence Date", value: "sentenceDate" },
    { label: "County", value: "county" },
    { label: "Case No.", value: "caseNo" },
    { label: "Sentence (YY-MM-DD)", value: "sentence" },
    { label: "Controlling Offense", value: "selectOffence" },
  ];

  const gender = GENDER_OPTIONS.find(
    (option) => option.value === inmate?.gender,
  );

  const selectedOffense = offenseWatch("selectedOffense");

  const sortedHistory = useMemo(() => {
    return [...(inmate?.offenseHistory || [])].sort(
      (a, b) =>
        new Date(b.offenseDate).getTime() - new Date(a.offenseDate).getTime(),
    );
  }, [inmate?.offenseHistory]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    offenseSetValue("selectedOffense", event.target.value);
  };

  const rows = sortedHistory.map((item) => ({
    ...item,
    select: (
      <Radio
        checked={selectedOffense === item._id}
        value={item?._id}
        onChange={handleRadioChange}
        name="selectedOffense"
      />
    ),
  }));

  /**
   * Submits the selected offense update to the server.
   * @param {any} values - Form values from the offense history form.
   */
  const handleOffenseSubmit = async (values: any) => {
    try {
      const findOffenceHistory = inmate?.offenseHistory?.find(
        (val: any) => val?._id === values?.selectedOffense,
      );
      const data = await updateOffenceHistory({
        selectedOffence: selectedOffense,
        primaryOffense: findOffenceHistory?.offense,
      });
      const message = data?.message || "Success";
      showSnackbar(message, "success");
    } catch (err: any) {
      const message = err?.response?.data?.message || "An error occurred";
      showSnackbar(message, "error");
    }
  };

  /**
   * Handles the Basic Info edit form submission.
   * Builds the payload with only editable fields and calls the updateInmate API.
   * @param {any} values - Form values from the edit form.
   */
  const handleEditSubmit = async (values: any) => {
    try {
      const payload = {
        isCountyInmate: caseData?.result?.contactId?.isCountyInmate,
        dob: values?.dob ? dayjs(values.dob).toISOString() : null,
        age: values?.age || "",
        gender: values?.gender || "",
        unit: values?.unit || "",
      };

      await updateInmate(payload);
      showSnackbar("Inmate info updated successfully", "success");
      setIsEditMode(false);
      if (refetchCase) {
        await refetchCase();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || "An error occurred";
      showSnackbar(message, "error");
    }
  };

  /** Cancels edit mode and resets the form to original inmate values */
  const handleCancelEdit = () => {
    resetEditForm(editInitialValues);
    setIsEditMode(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Grid container spacing={2}>
      {/* ── Basic Info Card ─────────────────────────────────────────────── */}
      <Grid item xs={12} md={6}>
        <SubCard
          title="Basic Info:"
          secondary={
            !isEditMode && caseData?.result?.contactId?.isCountyInmate ? (
              <Tooltip title="Edit Basic Info">
                <IconButton
                  size="small"
                  onClick={() => setIsEditMode(true)}
                  aria-label="edit-basic-info"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        >
          {/* ── VIEW MODE ──────────────────────────────────────────────── */}
          {!isEditMode && (
            <Grid container spacing={2}>
              {/* SID */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">SID #:</Typography>
                  <Typography variant="h6">
                    {inmate?.sidNumber || "-"}
                  </Typography>
                </Stack>
              </Grid>

              {/* TDCJ */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">TDCJ #:</Typography>
                  <Typography variant="h6">
                    {inmate?.tdcjNumber || "-"}
                  </Typography>
                </Stack>
              </Grid>

              {/* Inmate Name */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Inmate's Name:</Typography>
                  <Typography variant="h6">{inmate?.name || "-"}</Typography>
                </Stack>
              </Grid>

              {/* DOB */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">DOB:</Typography>
                  <Typography variant="h6">
                    {inmate?.dob
                      ? moment(inmate?.dob).format("MM/DD/YYYY")
                      : "-"}
                  </Typography>
                </Stack>
              </Grid>

              {/* Age */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Age:</Typography>
                  <Typography variant="h6">
                    {inmate?.dob
                      ? moment().diff(moment(inmate?.dob), "years")
                      : "-"}
                  </Typography>
                </Stack>
              </Grid>

              {/* Gender */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Gender:</Typography>
                  <Typography variant="h6">{gender?.label || "-"}</Typography>
                </Stack>
              </Grid>

              {/* Unit */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Unit:</Typography>
                  <Typography variant="h6">{inmate?.unit || "-"}</Typography>
                </Stack>
              </Grid>

              {/* Unit Location */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Unit Location:</Typography>
                  <Typography variant="h6">{location || "-"}</Typography>
                </Stack>
              </Grid>

              {/* Parole Release Date */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Parole Release Date:</Typography>
                  <Typography variant="h6">
                    {inmate.projectedReleaseDate
                      ? isValidDate(
                          moment(inmate?.projectedReleaseDate).format(
                            "MM/DD/YYYY",
                          ),
                        ) || "-"
                      : "-"}
                  </Typography>
                </Stack>
              </Grid>

              {/* Parole Eligibility Date */}
              <Grid item xs={12}>
                <Stack direction={"row"} alignItems={"center"} spacing={2}>
                  <Typography variant="h5">Parole Eligibility Date:</Typography>
                  <Typography variant="h6">
                    {caseData.result.contactId.isCountyInmate
                      ? isValidDate(
                          moment(
                            caseData?.result?.paroleEligibilityDate,
                          ).format("MM/DD/YYYY"),
                        ) || "-"
                      : isValidDate(
                          moment(inmate?.paroleEligibilityDate).format(
                            "MM/DD/YYYY",
                          ),
                        ) || "-"}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          )}

          {/* ── EDIT MODE ──────────────────────────────────────────────── */}
          {isEditMode && (
            <FormContainer
              onSuccess={handleEditSubmit}
              formContext={editFormContext}
            >
              <Grid container spacing={2}>
                {/* Inmate Name — read-only display only, not included in edit payload */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Inmate's Name"
                    value={inmate?.name || ""}
                    disabled
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderRadius: "12px" },
                      },
                    }}
                  />
                </Grid>

                {/* DOB */}
                <Grid item xs={12} sm={6}>
                  <BasicDatePicker
                    label="Date of Birth"
                    name="dob"
                    minDate={dayjs("1900-01-01")}
                    maxDate={dayjs()}
                  />
                </Grid>

                {/* Age */}
                <Grid item xs={12} sm={6}>
                  <MuiTextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    disabled
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={12} sm={6}>
                  <MuiTextField fullWidth label="Gender" name="gender" select>
                    {GENDER_OPTIONS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </MuiTextField>
                </Grid>

                {/* Unit */}
                <Grid item xs={12} sm={6}>
                  <MuiTextField fullWidth label="Unit" name="unit" />
                </Grid>

                {/* Unit Location — auto-derived, read-only */}
                <Grid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Unit Location"
                    name="unit_location"
                    disabled
                  />
                </Grid>

                {/* Parole Release Date */}
                {/* <Grid item xs={12} sm={6}>
                  <BasicDatePicker
                    label="Parole Release Date"
                    name="paroleReleaseDate"
                    minDate={dayjs("1900-01-01")}
                    // required={sid || tdcj ? true : false}
                  />
                </Grid> */}

                {/* Parole Eligibility Date */}
                {/* <Grid item xs={12} sm={6}>
                  <BasicDatePicker
                    label="Parole Eligibility Date"
                    name="paroleEligibilityDate"
                    minDate={dayjs("1900-01-01")}
                    // required={sid || tdcj ? true : false}
                  />
                </Grid> */}

                {/* Action Buttons — bottom right */}
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1}
                    mt={1}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCancelEdit}
                      disabled={isUpdatingInmate}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      type="submit"
                      disabled={isUpdatingInmate}
                      sx={{ textTransform: "none" }}
                    >
                      {isUpdatingInmate ? "Saving..." : "Confirm"}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </FormContainer>
          )}
        </SubCard>
      </Grid>

      {/* ── Parole Review Information Card ──────────────────────────────── */}
      <Grid item xs={12} md={6}>
        <SubCard title="Parole Review Information:">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Typography variant="h5">Parole Review Status:</Typography>
                <Typography variant="h6">
                  {inmate?.paroleReviewInfo?.paroleReviewStatus}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Typography variant="h5">Last Parole Decision:</Typography>
                <Typography variant="h6">
                  {inmate?.paroleReviewInfo?.lastParoleDecision}
                </Typography>
              </Stack>
            </Grid>

            {getParoleStatus(inmate?.paroleReviewInfo?.lastParoleDecision) ===
              "DENIED" && (
              <>
                <Grid item xs={12}>
                  <Stack direction="column" spacing={2}>
                    <Grid item xs={12}>
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        spacing={2}
                      >
                        <Typography variant="h5">Denial reason(s):</Typography>
                        <Typography variant="h6">
                          {denialReasonsCodes}
                        </Typography>
                      </Stack>
                    </Grid>

                    {inmate?.paroleReviewInfo?.denialReasons?.map(
                      (reason: any) => (
                        <Grid item xs={12} key={reason.code}>
                          <Typography variant="h5">{reason.heading}</Typography>
                          <Typography variant="h6" mt={1}>
                            {reason.value}
                          </Typography>
                        </Grid>
                      ),
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <Typography variant="h5">
                      Next Parole Review Date:
                    </Typography>
                    <Typography variant="h6">
                      {inmate?.paroleReviewInfo?.nextParoleDate}
                    </Typography>
                  </Stack>
                </Grid>
              </>
            )}
          </Grid>
        </SubCard>
      </Grid>

      {/* ── Offense History Card ─────────────────────────────────────────── */}
      <Grid item xs={12}>
        <FormContainer
          onSuccess={handleOffenseSubmit}
          formContext={offenseFormContext}
        >
          <SubCard
            title=" Offense History:"
            secondary={
              <Stack direction={"row"}>
                <Tooltip title={"Save Selected Offence"}>
                  <Button
                    color="success"
                    variant="contained"
                    type="submit"
                    size="small"
                    disabled={selectedOffense ? false : true}
                    sx={{
                      marginRight: 1,
                    }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </Stack>
            }
          >
            <BasicTable columns={columns} rows={rows || []} />
          </SubCard>
        </FormContainer>
      </Grid>
    </Grid>
  );
}

export default InmateInfo;
